/**
 * InterestSearch
 * Shared autocomplete search component for targeting options
 * Works across all platforms - adapts behavior based on platform prop
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, Plus, Check } from 'lucide-react';
import { debounce } from 'lodash';

export default function InterestSearch({
    platform,
    type = 'interest', // 'interest', 'behavior', 'skill', 'title', 'company', etc.
    brandId,
    userId,
    selectedItems = [],
    onSelect,
    onRemove,
    placeholder = 'Search targeting options...',
    maxSelections = 50,
    disabled = false
}) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Debounced search function
    const searchTargeting = useCallback(
        debounce(async (searchQuery) => {
            if (!searchQuery || searchQuery.length < 2) {
                setResults([]);
                return;
            }

            // Skip API call if required params are missing
            if (!brandId || !userId) {
                setError('Brand or user context not available');
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`/api/targeting/${platform}/search`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        brandId,
                        userId,
                        type,
                        query: searchQuery
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    // Check if connection is required
                    if (errorData.requiresConnection) {
                        throw new Error(`Connect your ${platform} account to search targeting options`);
                    }
                    throw new Error(errorData.error || 'Search failed');
                }

                const data = await response.json();
                setResults(data.results || []);
            } catch (err) {
                console.error('Targeting search error:', err);
                // Show more specific error message
                if (err.message.includes('Connect your')) {
                    setError(err.message);
                } else {
                    setError(err.message || 'Failed to search. Please try again.');
                }
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300),
        [platform, type, brandId, userId]
    );

    useEffect(() => {
        searchTargeting(query);
    }, [query, searchTargeting]);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !inputRef.current?.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (item) => {
        if (selectedItems.length >= maxSelections) {
            return;
        }

        // Check if already selected
        if (selectedItems.some(s => s.id === item.id)) {
            return;
        }

        onSelect(item);
        setQuery('');
        setResults([]);
        setIsOpen(false);
    };

    const isSelected = (item) => {
        return selectedItems.some(s => s.id === item.id);
    };

    const formatAudienceSize = (size) => {
        if (!size) return null;
        if (size >= 1000000000) return `${(size / 1000000000).toFixed(1)}B`;
        if (size >= 1000000) return `${(size / 1000000).toFixed(1)}M`;
        if (size >= 1000) return `${(size / 1000).toFixed(0)}K`;
        return size.toString();
    };

    return (
        <div className="interest-search">
            {/* Selected items */}
            {selectedItems.length > 0 && (
                <div className="selected-items">
                    {selectedItems.map(item => (
                        <span key={item.id} className="selected-tag">
                            <span className="tag-name">{item.name}</span>
                            {item.audienceSize && (
                                <span className="tag-size">
                                    {formatAudienceSize(item.audienceSize)}
                                </span>
                            )}
                            <button
                                type="button"
                                className="tag-remove"
                                onClick={() => onRemove(item)}
                                disabled={disabled}
                            >
                                <X size={12} />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Search input */}
            <div className="search-input-wrapper">
                <Search size={16} className="search-icon" />
                <input
                    ref={inputRef}
                    type="text"
                    className="search-input"
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    disabled={disabled || selectedItems.length >= maxSelections}
                />
                {loading && <Loader2 size={16} className="loading-icon" />}
            </div>

            {/* Dropdown results */}
            {isOpen && (query.length >= 2 || results.length > 0) && (
                <div ref={dropdownRef} className="search-dropdown">
                    {error && (
                        <div className="dropdown-error">{error}</div>
                    )}

                    {!error && results.length === 0 && query.length >= 2 && !loading && (
                        <div className="dropdown-empty">
                            No results found for "{query}"
                        </div>
                    )}

                    {results.map(item => (
                        <button
                            key={item.id}
                            type="button"
                            className={`dropdown-item ${isSelected(item) ? 'selected' : ''}`}
                            onClick={() => handleSelect(item)}
                            disabled={isSelected(item)}
                        >
                            <div className="item-content">
                                <span className="item-name">{item.name}</span>
                                {item.path && (
                                    <span className="item-path">{item.path}</span>
                                )}
                            </div>
                            <div className="item-meta">
                                {item.audienceSize && (
                                    <span className="item-size">
                                        {formatAudienceSize(item.audienceSize)} users
                                    </span>
                                )}
                                {isSelected(item) ? (
                                    <Check size={14} className="check-icon" />
                                ) : (
                                    <Plus size={14} className="add-icon" />
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Selection limit indicator */}
            {selectedItems.length >= maxSelections && (
                <div className="selection-limit">
                    Maximum {maxSelections} selections reached
                </div>
            )}

            <style>{`
                .interest-search {
                    position: relative;
                }

                .selected-items {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                    margin-bottom: 0.75rem;
                }

                .selected-tag {
                    display: inline-flex;
                    align-items: flex-start;
                    gap: 0.25rem;
                    padding: 0.375rem 0.5rem;
                    background: var(--color-primary);
                    color: var(--color-text-inverted);
                    border-radius: 4px;
                    font-size: 0.8125rem;
                    max-width: 100%;
                }

                .tag-name {
                    word-break: break-word;
                    line-height: 1.3;
                }

                .tag-size {
                    opacity: 0.8;
                    font-size: 0.75rem;
                }

                .tag-remove {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.125rem;
                    background: transparent;
                    border: none;
                    color: inherit;
                    cursor: pointer;
                    opacity: 0.7;
                    transition: opacity 0.2s;
                }

                .tag-remove:hover {
                    opacity: 1;
                }

                .search-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .search-icon {
                    position: absolute;
                    left: 0.75rem;
                    color: var(--color-text-tertiary);
                    pointer-events: none;
                }

                .search-input {
                    width: 100%;
                    padding: 0.625rem 0.75rem 0.625rem 2.25rem;
                    background: var(--color-bg-primary);
                    border: 1px solid var(--color-border);
                    border-radius: 6px;
                    font-size: 0.875rem;
                    color: var(--color-text-primary);
                }

                .search-input:focus {
                    outline: none;
                    border-color: var(--color-primary);
                }

                .search-input:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .loading-icon {
                    position: absolute;
                    right: 0.75rem;
                    color: var(--color-text-tertiary);
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .search-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    margin-top: 0.25rem;
                    max-height: 300px;
                    overflow-y: auto;
                    background: var(--color-surface);
                    border: 1px solid var(--color-border);
                    border-radius: 6px;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
                    z-index: 10000;
                }

                .dropdown-error,
                .dropdown-empty {
                    padding: 0.75rem;
                    text-align: center;
                    color: var(--color-text-tertiary);
                    font-size: 0.875rem;
                }

                .dropdown-error {
                    color: var(--color-error);
                }

                .dropdown-item {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 0.75rem;
                    width: 100%;
                    padding: 0.75rem;
                    background: transparent;
                    border: none;
                    border-bottom: 1px solid var(--color-border);
                    text-align: left;
                    cursor: pointer;
                    transition: background 0.15s;
                    color: var(--color-text-primary);
                }

                .dropdown-item:last-child {
                    border-bottom: none;
                }

                .dropdown-item:hover:not(:disabled) {
                    background: var(--color-bg-secondary);
                }

                .dropdown-item.selected {
                    opacity: 0.5;
                    cursor: default;
                }

                .item-content {
                    flex: 1;
                    min-width: 0;
                }

                .item-name {
                    display: block;
                    font-size: 0.875rem;
                    color: var(--color-text-primary);
                    word-break: break-word;
                    line-height: 1.4;
                }

                .item-path {
                    display: block;
                    font-size: 0.75rem;
                    color: var(--color-text-tertiary);
                    word-break: break-word;
                    line-height: 1.3;
                    margin-top: 0.125rem;
                }

                .item-meta {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex-shrink: 0;
                    padding-top: 0.125rem;
                }

                .item-size {
                    font-size: 0.75rem;
                    color: var(--color-text-tertiary);
                }

                .check-icon {
                    color: var(--color-primary);
                }

                .add-icon {
                    color: var(--color-text-tertiary);
                }

                .selection-limit {
                    margin-top: 0.5rem;
                    font-size: 0.75rem;
                    color: var(--color-text-tertiary);
                    text-align: center;
                }
            `}</style>
        </div>
    );
}
